import React, { useState, useEffect } from 'react';
import { 
  Mail, 
  Send, 
  Users, 
  UserCheck, 
  Crown, 
  Eye, 
  Save,
  FileText,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { useToast } from '../../hooks/use-toast';
import { useMutation, useQuery } from '@tanstack/react-query';
import adminEmailService, { EmailBroadcastData, EmailTemplate } from '../../services/adminEmailService';
import { UserSearchFilters } from '../../services/adminUserService';
import adminUserService, { UserSummary } from '../../services/adminUserService';

interface EmailBroadcastDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUserIds: string[];
  currentFilters: UserSearchFilters;
  totalUsers: number;
}

const EmailBroadcastDialog: React.FC<EmailBroadcastDialogProps> = ({
  isOpen,
  onClose,
  selectedUserIds,
  currentFilters,
  totalUsers
}) => {
  const { toast } = useToast();
  
  const [recipientType, setRecipientType] = useState<'all' | 'selected' | 'custom'>('all');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [emailType, setEmailType] = useState<'announcement' | 'marketing' | 'system' | 'newsletter'>('announcement');
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [previewData, setPreviewData] = useState<{ recipients_count: number; sample_recipients: string[] } | null>(null);
  const [customSelectedUsers, setCustomSelectedUsers] = useState<string[]>([]);
  const [showUserPicker, setShowUserPicker] = useState(false);

  // Debug custom selected users changes
  useEffect(() => {
    console.log('🎯 customSelectedUsers changed:', customSelectedUsers);
  }, [customSelectedUsers]);

  // Queries
  const { data: templates } = useQuery({
    queryKey: ['email-templates'],
    queryFn: adminEmailService.getEmailTemplates,
    enabled: isOpen
  });

  const { data: allUsers } = useQuery({
    queryKey: ['all-users-for-email'],
    queryFn: () => adminUserService.getUsers(1, 100, {}),
    enabled: isOpen
  });

  // Mutations
  const previewMutation = useMutation({
    mutationFn: adminEmailService.previewEmail,
    onSuccess: (data) => {
      setPreviewData(data);
    },
    onError: (error: any) => {
      toast({
        title: 'Preview Error',
        description: error.message || 'Failed to preview email',
        variant: 'destructive'
      });
    }
  });

  const sendEmailMutation = useMutation({
    mutationFn: adminEmailService.sendBroadcastEmail,
    onSuccess: (data) => {
      toast({
        title: 'Email Sent Successfully!',
        description: `Email sent to ${data.recipients_count} recipients`,
      });
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: 'Send Failed',
        description: error.message || 'Failed to send email',
        variant: 'destructive'
      });
    }
  });

  // Effects
  useEffect(() => {
    if (isOpen && selectedUserIds.length > 0) {
      setRecipientType('selected');
    }
  }, [isOpen, selectedUserIds]);

  useEffect(() => {
    if (isOpen) {
      setCustomSelectedUsers([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      handlePreview();
    }
  }, [recipientType, currentFilters, selectedUserIds, customSelectedUsers, isOpen]);

  // Handlers
  const resetForm = () => {
    setSubject('');
    setMessage('');
    setEmailType('announcement');
    setSelectedTemplate('');
    setPreviewData(null);
    setCustomSelectedUsers([]);
    setShowUserPicker(false);
  };

  const handleTemplateSelect = (templateId: string) => {
    const template = templates?.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject);
      setMessage(template.content);
      setSelectedTemplate(templateId);
    }
  };

  const handlePreview = () => {
    console.log('🔍 Preview Debug:', {
      recipientType,
      selectedUserIds: selectedUserIds.length,
      customSelectedUsers: customSelectedUsers.length,
      customSelectedUsersArray: customSelectedUsers
    });

    const previewData: Omit<EmailBroadcastData, 'subject' | 'message'> = {
      recipient_type: recipientType === 'custom' ? 'selected' : recipientType, // Map 'custom' to 'selected' for backend
      email_type: emailType
    };

    if (recipientType === 'selected') {
      previewData.user_ids = selectedUserIds;
    } else if (recipientType === 'custom') {
      previewData.user_ids = customSelectedUsers;
    }

    console.log('📤 Sending preview request:', previewData);
    previewMutation.mutate(previewData);
  };

  const handleSend = () => {
    if (!subject.trim() || !message.trim()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in both subject and message',
        variant: 'destructive'
      });
      return;
    }

    console.log('📧 Sending email with data:', {
      recipientType,
      selectedUserIds: selectedUserIds.length,
      customSelectedUsers: customSelectedUsers.length,
      customSelectedUsersArray: customSelectedUsers
    });

    const emailData: EmailBroadcastData = {
      recipient_type: recipientType === 'custom' ? 'selected' : recipientType, // Map 'custom' to 'selected' for backend
      subject: subject.trim(),
      message: message.trim(),
      email_type: emailType
    };

    if (recipientType === 'selected') {
      emailData.user_ids = selectedUserIds;
    } else if (recipientType === 'custom') {
      emailData.user_ids = customSelectedUsers;
    }

    console.log('📤 Final email data being sent:', emailData);
    sendEmailMutation.mutate(emailData);
  };

  const getRecipientTypeInfo = () => {
    switch (recipientType) {
      case 'all':
        return {
          icon: Users,
          label: 'All Users',
          description: `Send to all ${totalUsers} users in the system`,
          color: 'bg-blue-500'
        };
      case 'selected':
        return {
          icon: UserCheck,
          label: 'Pre-Selected Users',
          description: `Send to ${selectedUserIds.length} users selected in the table`,
          color: 'bg-green-500'
        };
      case 'custom':
        return {
          icon: Crown,
          label: 'Custom Selection',
          description: `Send to ${customSelectedUsers.length} custom selected users`,
          color: 'bg-purple-500'
        };
    }
  };



  const recipientInfo = getRecipientTypeInfo();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
            <Mail className="w-6 h-6 text-blue-600" />
            Send Email Broadcast
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Email Templates */}
            {templates && templates.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Email Templates
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          {template.name} - {template.type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
            )}

            {/* Email Type */}
            <div>
              <Label className="text-base font-semibold">Email Type</Label>
              <Select value={emailType} onValueChange={(value: any) => setEmailType(value)}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="announcement">📢 Announcement</SelectItem>
                  <SelectItem value="marketing">🎯 Marketing</SelectItem>
                  <SelectItem value="system">⚙️ System Update</SelectItem>
                  <SelectItem value="newsletter">📰 Newsletter</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div>
              <Label className="text-base font-semibold">Subject</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter email subject..."
                className="mt-2"
              />
            </div>

            {/* Message */}
            <div>
              <Label className="text-base font-semibold">Message</Label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Enter your email message..."
                rows={8}
                className="mt-2 resize-none"
              />
              <p className="text-sm text-gray-500 mt-1">
                You can use basic HTML tags for formatting
              </p>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Recipient Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Recipients
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup value={recipientType} onValueChange={(value: any) => setRecipientType(value)}>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="all" id="all" />
                    <Label htmlFor="all" className="flex items-center gap-2 cursor-pointer">
                      <Users className="w-4 h-4" />
                      All Users ({totalUsers})
                    </Label>
                  </div>
                  
                  {selectedUserIds.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="selected" id="selected" />
                      <Label htmlFor="selected" className="flex items-center gap-2 cursor-pointer">
                        <UserCheck className="w-4 h-4" />
                        Pre-Selected Users ({selectedUserIds.length})
                      </Label>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="custom" id="custom" />
                    <Label htmlFor="custom" className="flex items-center gap-2 cursor-pointer">
                      <Crown className="w-4 h-4" />
                      Custom Selection ({customSelectedUsers.length})
                    </Label>
                  </div>
                </RadioGroup>

                {recipientType === 'custom' && (
                  <div className="mt-4">
                    <Button
                      variant="outline"
                      onClick={() => {
                        console.log('🔘 Select Users button clicked');
                        console.log('📊 Current state:', { showUserPicker, customSelectedUsers });
                        setShowUserPicker(true);
                      }}
                      className="w-full"
                    >
                      <UserCheck className="w-4 h-4 mr-2" />
                      Select Users ({customSelectedUsers.length} selected)
                    </Button>
                    
                    {customSelectedUsers.length > 0 && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                            {customSelectedUsers.length} user{customSelectedUsers.length !== 1 ? 's' : ''} selected
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setCustomSelectedUsers([])}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Clear All
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Preview
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className={`w-10 h-10 ${recipientInfo.color} rounded-lg flex items-center justify-center`}>
                      <recipientInfo.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold">{recipientInfo.label}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {recipientInfo.description}
                      </p>
                    </div>
                  </div>

                  {previewMutation.isPending ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Loading preview...
                    </div>
                  ) : previewData ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm font-semibold">
                          {previewData.recipients_count} recipients
                        </span>
                      </div>
                      {previewData.sample_recipients.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-1">Sample recipients:</p>
                          <div className="space-y-1">
                            {previewData.sample_recipients.slice(0, 3).map((email, index) => (
                              <Badge key={index} variant="outline" className="text-xs">
                                {email}
                              </Badge>
                            ))}
                            {previewData.sample_recipients.length > 3 && (
                              <Badge variant="outline" className="text-xs">
                                +{previewData.sample_recipients.length - 3} more
                              </Badge>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreview}
                      className="w-full"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview Recipients
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>



            {/* Warning */}
            <Card className="border-amber-200 bg-amber-50 dark:bg-amber-900/20">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                      Important Notice
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                      This will send real emails to users. Please review your message carefully before sending.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handlePreview}
              disabled={previewMutation.isPending}
            >
              <Eye className="w-4 h-4 mr-2" />
              {previewMutation.isPending ? 'Loading...' : 'Preview'}
            </Button>
            
            <Button
              onClick={handleSend}
              disabled={!subject.trim() || !message.trim() || sendEmailMutation.isPending}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
            >
              {sendEmailMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>

      {/* User Selection Modal */}
      <Dialog open={showUserPicker} onOpenChange={setShowUserPicker}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-blue-600" />
              Select Users for Email
            </DialogTitle>
          </DialogHeader>

          <UserSelectionModal
            allUsers={allUsers?.users || []}
            selectedUserIds={customSelectedUsers}
            onSelectionChange={setCustomSelectedUsers}
            onClose={() => setShowUserPicker(false)}
          />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

// User Selection Modal Component
interface UserSelectionModalProps {
  allUsers: UserSummary[];
  selectedUserIds: string[];
  onSelectionChange: (userIds: string[]) => void;
  onClose: () => void;
}

const UserSelectionModal: React.FC<UserSelectionModalProps> = ({
  allUsers,
  selectedUserIds,
  onSelectionChange,
  onClose
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'admin' | 'user'>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Filter users based on search and filter type
  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      (filterType === 'admin' && user.is_admin) ||
      (filterType === 'user' && !user.is_admin);
    
    return matchesSearch && matchesFilter;
  });

  // Paginate users
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
  const startIndex = (currentPage - 1) * usersPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + usersPerPage);

  const handleSelectAll = () => {
    const allFilteredIds = filteredUsers.map(user => user.id);
    const newSelection = [...new Set([...selectedUserIds, ...allFilteredIds])];
    onSelectionChange(newSelection);
  };

  const handleDeselectAll = () => {
    const filteredIds = new Set(filteredUsers.map(user => user.id));
    const newSelection = selectedUserIds.filter(id => !filteredIds.has(id));
    onSelectionChange(newSelection);
  };

  const handleUserToggle = (userId: string) => {
    console.log('🔄 User toggle clicked:', { userId, currentSelection: selectedUserIds });
    
    if (selectedUserIds.includes(userId)) {
      const newSelection = selectedUserIds.filter(id => id !== userId);
      console.log('➖ Removing user, new selection:', newSelection);
      onSelectionChange(newSelection);
    } else {
      const newSelection = [...selectedUserIds, userId];
      console.log('➕ Adding user, new selection:', newSelection);
      onSelectionChange(newSelection);
    }
  };

  const selectedOnCurrentPage = paginatedUsers.filter(user => selectedUserIds.includes(user.id)).length;
  const allOnCurrentPageSelected = paginatedUsers.length > 0 && selectedOnCurrentPage === paginatedUsers.length;

  return (
    <div className="space-y-4">
      {/* Search and Filter */}
      <div className="flex gap-3">
        <div className="flex-1">
          <Input
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full"
          />
        </div>
        <Select value={filterType} onValueChange={(value: any) => {
          setFilterType(value);
          setCurrentPage(1);
        }}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="admin">Admins</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Selection Summary */}
      <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
        <div className="text-sm">
          <span className="font-medium">{selectedUserIds.length}</span> of{' '}
          <span className="font-medium">{allUsers.length}</span> users selected
          {filteredUsers.length !== allUsers.length && (
            <span className="text-gray-500 ml-2">
              ({filteredUsers.length} shown)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleSelectAll}
            disabled={filteredUsers.length === 0}
          >
            Select All Shown
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDeselectAll}
            disabled={selectedOnCurrentPage === 0}
          >
            Deselect All Shown
          </Button>
        </div>
      </div>

      {/* User List */}
      <div className="border rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          {paginatedUsers.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No users found matching your criteria
            </div>
          ) : (
            <div className="divide-y">
              {paginatedUsers.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  onClick={() => handleUserToggle(user.id)}
                >
                  <Checkbox
                    checked={selectedUserIds.includes(user.id)}
                    onCheckedChange={() => handleUserToggle(user.id)}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">
                        {user.first_name && user.last_name 
                          ? `${user.first_name} ${user.last_name}` 
                          : user.email}
                      </p>
                      {user.is_admin && (
                        <Badge variant="outline" className="text-xs">Admin</Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  <div className="text-xs text-gray-400">
                    {user.credits} credits
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={() => {
            console.log('✅ Done button clicked with selection:', selectedUserIds);
            onClose();
          }} 
          className="bg-blue-600 hover:bg-blue-700"
        >
          Done ({selectedUserIds.length} selected)
        </Button>
      </div>
    </div>
  );
};

export default EmailBroadcastDialog;